// © Microsoft Corporation. All rights reserved.

import { ActivityData, User } from "@msnews/social-data-service";
import { ComponentConnector, FollowInfo, PopupConnector, UsersConnector } from "../../Social.props";
import { ProfileActivityItemTelemetryObjects, profileActivityItemTelemetryContracts } from "./ProfileActivity.telemetry";
import { TelemetryEventMap, addOrUpdateTelemetryObject, dispatchTelemetryEvent, renderHybrid } from "../../Social.telemetry";
import { chevron_down, exclamation_round } from "../../utils/icons";

import MostReactions from "../../common/most-reactions/MostReactions";
import ProfileActivityReadMarker from "./ProfileActivityReadMarker";
import React from "react";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialLocalizedStrings } from "../../Social.localizedStrings";
import { SocialState } from "../../Social.state";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import UserCard from "../../comment/user-card/UserCard";
import { classNames } from "@microsoft/fast-web-utilities";
import { connect } from "react-redux";
import {enums} from "../../utils/enums";
import getAgoText from "../../utils/getAgoText";
import { hasThemeBase } from "../../utils/themeHelpers";
import openArticlePage from "../../utils/openArticlePage";
import openGuidelinePage from "../../utils/openGuidelinePage";
import openProfilePage from "../../utils/openProfilePage";

interface ProfileReactionActivityState {
    miniReactionBreakdownVisible: boolean;
    userCardVisible: boolean;
    userCardUserId: string;
    isHoverUserCard: boolean;
    isHoverMiniReactionBreakdown: boolean;
    messageTruncated: boolean;
}

export interface ProfileReactionActivityProps {
    componentConnector: ComponentConnector;
    activity: ActivityData;
    me: User;
    managedClasses?: SocialClassNameContract;
    users: { [id: string]: FollowInfo;}; // dictionary definening
    usersConnector: UsersConnector;
    t: SocialLocalizedStrings;
    popupConnector: PopupConnector;
    market: string;
    mainTelemetryObject: TelemetryObjectBase;
    theme?: string;
    responsiveClass: string;
}

const mapStateToProps = function(state: SocialState) {
    return {
        theme: state.theme
    };
};

class ProfileReactionActivity extends React.Component<ProfileReactionActivityProps, ProfileReactionActivityState> {
    activityItemRef: HTMLLIElement;
    reactionMenuIconRef: React.RefObject<HTMLElement>;
    telemetryObjects: ProfileActivityItemTelemetryObjects;
    messageRef: React.RefObject<HTMLDivElement>;
    isLoaded: boolean;

    constructor(props) {
        super(props);
        this.state = {
            miniReactionBreakdownVisible: false,
            userCardVisible: false,
            userCardUserId: "",
            isHoverUserCard: false,
            isHoverMiniReactionBreakdown: false,
            messageTruncated: null
        };

        this.activityItemRef = null;
        this.reactionMenuIconRef = React.createRef<HTMLElement>();
        this.messageRef = React.createRef();
    }

    componentDidMount() {
        if (this.props.activity.target.comment.mentionUser && !(this.props.users && this.props.users[this.props.activity.target.comment.mentionUser])) {
            this.props.usersConnector.fetchUser(this.props.activity.target.comment.mentionUser);
        }
        this.checkMessageTruncated();
        this.loadedHandler();
    }

    componentDidUpdate() {
        this.checkMessageTruncated();
        this.loadedHandler();
    }

    checkMessageTruncated() {
        /** Check if message is truncated by webkit clamp */
        if (this.messageRef && this.messageRef.current && this.state.messageTruncated == null) {
            this.messageRef.current.scrollHeight > this.messageRef.current.clientHeight + 10
                ? this.setState({messageTruncated: true})
                : this.setState({messageTruncated: false});
        }
    }

    onReactionsClick(id) {
        this.props.popupConnector.openPopup("reactionBreakdownComment", {id, parentTelemetryObject: addOrUpdateTelemetryObject(this.props.mainTelemetryObject, profileActivityItemTelemetryContracts.commentReactionBreakdown, {}), focusBackRef: this.reactionMenuIconRef.current});
    }

    showUserCard(e, userId, isHoverUserCard=true) {
        this.setState({userCardVisible: true, userCardUserId: userId, isHoverUserCard: isHoverUserCard});
    }

    render() {
        const { responsiveClass, componentConnector, activity, managedClasses, t, me, usersConnector, users, market, mainTelemetryObject, theme } = this.props;
        const reactionCounts = activity.target.reactionCounts;
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);
        const { messageTruncated } = this.state;

        /** Generate Telemetry Objects */
        this.telemetryObjects = {
            activityItem: addOrUpdateTelemetryObject(mainTelemetryObject, profileActivityItemTelemetryContracts.activityItem, { id: activity.id}, this.telemetryObjects && this.telemetryObjects.activityItem)
        };

        // Reaction Counts
        const totalReactionCount = reactionCounts ? Object.values(reactionCounts).reduce(($sum, $count) => $sum + $count, 0) : 0;

        let commentBody;
        if (activity.target.comment.isDeleted) {
            commentBody =
                <div className="cwt-body">
                    <span className="cwt-link cwt-comment-deleted-message" role="link" title={t.DisabledMessagesGuidelines} {...openGuidelinePage(componentConnector, theme, market, "_blank")}>
                        {t[enums.visibleStatusMap[activity.target.comment.visibleStatus] || enums.visibleStatusMap.Default]}
                        <i className="cwt-icon cwt-disabled-message">{exclamation_round}</i>
                    </span>
                </div>;
        } else {
            const mentionUserId = activity.target.comment.mentionUser;
            commentBody =
            <div className="cwt-body">
                <div className={messageTruncated == null || messageTruncated ? "cwt-clamp" : ""} ref={this.messageRef}>
                    {mentionUserId &&
                <React.Fragment>
                    <a
                        className="cwt-name cwt-link"
                        onMouseEnter={(e) => this.showUserCard(e, mentionUserId)}
                        {...openProfilePage(componentConnector, theme, mentionUserId, market)}
                    >{users && users[mentionUserId] && users[mentionUserId].data && users[mentionUserId].data.name}</a>
                    { !isSuperApp &&
                            <i className="cwt-link cwt-visible-on-focus" role="button" aria-label={t.AriaLabelsShowUserCard} onClick={(e) => this.showUserCard(e, mentionUserId, false)}> {chevron_down} </i>
                    }
                    &nbsp;
                </React.Fragment>
                    }
                    <a {...openArticlePage(theme, activity.target.url, activity.target.comment.contentId, activity.target.comment.id, "_blank")}>
                        <div dangerouslySetInnerHTML={{__html: activity.target.body}} />
                    </a>
                </div>
                { messageTruncated && <label className="cwt-show-clamped-btn cwt-link" role="button" onClick={() => this.setState({messageTruncated: false})}>{t.CommentItemSeeMore}</label>}
            </div>;
        }

        return (
            <li
                key={activity.id.toString()}
                className={classNames(
                    managedClasses.profileActivityContainer,
                    managedClasses.profileActivityReactionContainer,
                    !activity.isSeen ? "new" : "",
                    [managedClasses.profileActivitySuperAppContainer, isSuperApp],
                    [managedClasses.profileActivityReactionSuperAppContainer, isSuperApp],
                    responsiveClass
                )}
                {...renderHybrid(this.telemetryObjects.activityItem, true, (el) => { this.activityItemRef = el; })}
            >
                {this.activityItemRef && <ProfileActivityReadMarker {...{activity, usersConnector, activityItemRef: this.activityItemRef}} />}
                <div className="cwt-start">
                    <MostReactions
                        containerClassName="cwt-reaction-counts"
                        onReactionsClick={e => this.onReactionsClick(activity.target.comment.id)}
                        hideLabel={true}
                        {...{reactionCounts, t, managedClasses, theme}}
                    />

                    { !isSuperApp &&
                        <i className="cwt-link cwt-visible-on-focus"
                            role="button"
                            aria-label= {t.AriaLabelsShowMiniReactionBreakdownPopup}
                            onClick={() => this.setState({miniReactionBreakdownVisible: !this.state.miniReactionBreakdownVisible, isHoverMiniReactionBreakdown: false})}
                            onBlur={()=>{ this.setState({miniReactionBreakdownVisible: false, isHoverMiniReactionBreakdown: false}); }}>
                            {chevron_down}
                        </i>
                    }
                </div>
                {this.state.userCardVisible &&
                <UserCard
                    {...{usersConnector, users, managedClasses, me, t, id: this.state.userCardUserId, visible: true, market,
                        onHide: () => this.setState({userCardVisible: false, isHoverUserCard: false}),
                        triggerRef: { current: this.activityItemRef } as React.Ref<HTMLElement>,
                        isHover: this.state.isHoverUserCard,
                        mainTelemetryObject
                    }}
                />
                }
                <div className="cwt-mid">
                    <div className="cwt-text">
                        { `${totalReactionCount} ${totalReactionCount > 1 ? t.ProfileActivityReactionPluralMsg : t.ProfileActivityReactionMsg} `}
                        <a className="cwt-link" {...openArticlePage(theme, activity.target.url, activity.target.comment.contentId, activity.target.comment.id, "_blank")}> {t.CommentLink} </a>
                        {commentBody}
                    </div>
                    <div className="cwt-sub-text"> {getAgoText(activity.datetime)} </div>
                </div>
                <div className="cwt-end"></div>
            </li>
        );
    }

    loadedHandler() {
        if (this.isLoaded) { return; }

        if (this.activityItemRef) {
            dispatchTelemetryEvent({...TelemetryEventMap.genericLoaded, sendPeregrine: true}, this.activityItemRef);
            this.isLoaded = true;
        }
    }
}

export default connect(mapStateToProps)(ProfileReactionActivity);